import React, { useRef, useEffect, useState, useCallback } from 'react';
import './ShirtPreviewModal.css';
import MASK_FRONT from '../../assets/1.png';
import MASK_BACK  from '../../assets/2.png';

const PREVIEW_CANVAS_WIDTH = 736;
const PREVIEW_CANVAS_HEIGHT = 980;

// --- Position maps ------------------------------------------------------------
const LOCATION_PHOTO_POS = {
  'Full Front':     { top: '27%', left: '26.5%', width: '48%', height: '38%', clipShape: 'rect' },
  'Medium Front':   { top: '27%', left: '30%',   width: '40%', height: '28%', clipShape: 'rect' },
  'Center Chest':   { top: '27%', left: '36%',   width: '28%', height: '20%', clipShape: 'rect' },
  'Across Chest':   { top: '27%', left: '24%',   width: '52%', height: '13%', clipShape: 'rect' },
  'Right Chest':    { top: '27%', left: '27%',   width: '18%', height: '13%', clipShape: 'rect' },
  'Left Chest':     { top: '27%', left: '56%',   width: '18%', height: '13%', clipShape: 'rect' },
  'Right Vertical': { top: '27%', left: '27%',   width: '14%', height: '44%', clipShape: 'rect' },
  'Left Vertical':  { top: '27%', left: '60%',   width: '14%', height: '44%', clipShape: 'rect' },
  'Bottom Right':   { top: '72%', left: '26%',   width: '23%', height: '12%', clipShape: 'rect' },
  'Bottom Left':    { top: '72%', left: '53%',   width: '23%', height: '12%', clipShape: 'rect' },
  'Right Sleeve':   { clipShape: 'sleeve', cx: 14.7, cy: 35.4, dw: 14.0, dh: 13.3, rotation: 34  },
  'Left Sleeve':    { clipShape: 'sleeve', cx: 85.2, cy: 35.4, dw: 14.0, dh: 13.3, rotation: -34 },
  'Full Back':           { top: '22%', left: '26.5%', width: '48%', height: '38%', clipShape: 'rect' },
  'Medium Back':         { top: '22%', left: '30%',   width: '40%', height: '28%', clipShape: 'rect' },
  'Locker Center Patch': { top: '22%', left: '43%',   width: '14%', height: '11%', clipShape: 'rect' },
  'Back Center':         { top: '22%', left: '27.5%', width: '45%', height: '13%', clipShape: 'rect' },
  'Across Shoulder':     { top: '22%', left: '18%',   width: '64%', height: '10%', clipShape: 'rect' },
  'Left Sleeve Back':    { clipShape: 'sleeve', cx: 15.9, cy: 37.4, dw: 12.1, dh: 12.2, rotation: 34  },
  'Right Sleeve Back':   { clipShape: 'sleeve', cx: 84.0, cy: 37.4, dw: 12.1, dh: 12.2, rotation: -34 },
};

export const LOCATION_SHIRT_POS = LOCATION_PHOTO_POS;

const RECTANGLE_PREVIEW_SIZE_OVERRIDES = {
  'Full Front': { w: 11.7, h: 16.5 },
};

function getDerivedRectPosition(basePos, size) {
  if (!basePos || !size || basePos.clipShape !== 'rect') return basePos;

  const widthPct = parseFloat(basePos.width);
  const currentHeightPct = parseFloat(basePos.height);
  const currentTopPct = parseFloat(basePos.top);
  const derivedHeightPct = (widthPct * PREVIEW_CANVAS_WIDTH * size.h) / (PREVIEW_CANVAS_HEIGHT * size.w);
  const centeredTopPct = currentTopPct - ((derivedHeightPct - currentHeightPct) / 2) + 6;


  return {
    ...basePos,
    top: `${centeredTopPct}%`,
    height: `${derivedHeightPct}%`,
  };
}

function getLocationPhotoPosition(loc, isCircle) {
  const basePos = LOCATION_PHOTO_POS[loc];
  if (!basePos || isCircle) return basePos;

  const rectSize = RECTANGLE_PREVIEW_SIZE_OVERRIDES[loc];
  if (!rectSize) return basePos;

  return getDerivedRectPosition(basePos, rectSize);
}

const BACK_LOCS   = new Set(['Full Back','Medium Back','Back Center','Locker Center Patch','Across Shoulder','Left Sleeve Back','Right Sleeve Back']);
const SLEEVE_LOCS = new Set(['Left Sleeve','Right Sleeve']);

// --- Color map ----------------------------------------------------------------
const COLOR_MAP = {
  white:'#ffffff', black:'#2a2a2a', navy:'#1a2a4a',
  red:'#c0392b', blue:'#2471a3', gray:'#909090', grey:'#909090',
  green:'#1e8449', yellow:'#f1c40f', orange:'#e67e22', pink:'#e91e8c',
  purple:'#7d3c98', maroon:'#7b241c', brown:'#784212', beige:'#d2b48c',
  'light blue':'#5dade2', 'royal blue':'#2450a0',
  'forest green':'#1a5c2a', 'sky blue':'#87ceeb',
};

function resolveColor(colorStr) {
  if (!colorStr) return null;
  const lower = colorStr.trim().toLowerCase();
  if (lower === 'white' || lower === '#ffffff' || lower === '#f5f5f5') return null;
  if (/^#[0-9a-f]{3,8}$/i.test(lower) || /^rgb/i.test(lower)) return colorStr;
  if (COLOR_MAP[lower]) return COLOR_MAP[lower];
  let best = null, bestLen = 0;
  for (const [name, hex] of Object.entries(COLOR_MAP)) {
    if (lower.includes(name) && name.length > bestLen) { best = hex; bestLen = name.length; }
  }
  return best;
}

// --- Sleeve draw settings -----------------------------------------------------
const SLEEVE_SETTING = {
  'Left Sleeve':       { cx: 89.0, cy: 32.0, dw: 15.5, dh: 15.5, rot: -34 },
  'Right Sleeve':      { cx: 11.5, cy: 32.0, dw: 15.5, dh: 17.5, rot:  34 },
  'Left Sleeve Back':  { cx: 11.5, cy: 32.0, dw: 15.5, dh: 15.5, rot:  34 },
  'Right Sleeve Back': { cx: 89.0, cy: 32.0, dw: 15.5, dh: 17.5, rot: -34 },
};

// =============================================================================
// --- Conflict rule table -----------------------------------------------------
// =============================================================================
//
// Each rule defines a pair of locations that conflict when both are active.
// `adjustments` lists what to do to which location.
//
// Fields per adjustment:
//   target       – location key to transform
//   scaleW       – multiply width  (1 = no change)
//   scaleH       – multiply height (1 = no change, 0 = hide)
//   shiftYpct    – shift Y by this fraction of canvas height (positive = down)
//   shiftXpct    – shift X by this fraction of canvas width
//   center       – re-center inside original bounds after scaling
//
// Transforms accumulate across all matching rules (scales multiply, shifts add).

const CONFLICT_RULES = [
  // ── Across Chest conflicts ──────────────────────────────────────────────────
  {
    pair: ['Across Chest', 'Medium Front'],
    adjustments: [
      { target: 'Across Chest', scaleH: 0.80, center: true },
      { target: 'Medium Front', shiftYpct: 0.11 },
    ],
  },
  {
    pair: ['Across Chest', 'Center Chest'],
    adjustments: [
      { target: 'Center Chest', shiftYpct: 0.10 },
    ],
  },
  {
    pair: ['Across Chest', 'Left Chest'],
    adjustments: [
      // Left Chest is fully covered by Across Chest — hide it
      { target: 'Left Chest', scaleH: 0 },
    ],
  },
  {
    pair: ['Across Chest', 'Right Chest'],
    adjustments: [
      { target: 'Right Chest', scaleH: 0 },
    ],
  },

// center chest conflicts ─────────────────────────────────────────────────
{
  pair: ['Right Chest', 'Left Chest'],
  adjustments: [
    { target: 'Right Chest',  scaleW: 0.75, scaleH: 0.75, center: true, shiftXpct:  -0.04  },
    { target: 'Left Chest',   scaleW: 0.75, scaleH: 0.75, center: true, shiftXpct:  0.03  },
    { target: 'Center Chest', scaleW: 0.75, scaleH: 0.75, center: true },
  ],
},

// ── Back conflicts ──────────────────────────────────────────────────────────
{
  pair: ['Locker Center Patch', 'Medium Back'],
  adjustments: [
    { target: 'Medium Back', shiftYpct: 0.11 },
  ],
},
{
  pair: ['Back Center', 'Medium Back'],
  adjustments: [
    { target: 'Medium Back', shiftYpct: 0.11 },
    { target: 'Back Center', scaleH: 0.75 },
  ],
},

  // ── Left Vertical conflicts ─────────────────────────────────────────────────
  {
    pair: ['Left Vertical', 'Medium Front'],
    adjustments: [
      { target: 'Left Vertical', scaleW: 0.60, shiftXpct: 0.06 },
      { target: 'Medium Front', scaleW: 0.80, center: true, shiftXpct: 0 },
    ],
  },
  {
    pair: ['Left Vertical', 'Center Chest'],
    adjustments: [{ target: 'Center Chest', scaleW: 0.80, center: true }],
  },
  {
    pair: ['Left Vertical', 'Across Chest'],
    adjustments: [
      { target: 'Across Chest', scaleW: 0.82, center: true },
      { target: 'Left Vertical', shiftYpct: 0.10 },
    ],
  },
  {
    pair: ['Left Vertical', 'Right Chest'],
    adjustments: [{ target: 'Right Chest', scaleW: 0.75, center: true }],
  },

  // ── Right Vertical conflicts ────────────────────────────────────────────────
  {
    pair: ['Right Vertical', 'Medium Front'],
    adjustments: [
      { target: 'Right Vertical', scaleW: 0.60, shiftXpct: 0.0 },
      { target: 'Medium Front', scaleW: 0.80, center: true, shiftXpct: 0 },
    ],
  },
  {
    pair: ['Right Vertical', 'Center Chest'],
    adjustments: [{ target: 'Center Chest', scaleW: 0.80, center: true }],
  },
  {
    pair: ['Right Vertical', 'Across Chest'],
    adjustments: [
      { target: 'Across Chest', scaleW: 0.82, center: true },
      { target: 'Right Vertical', shiftYpct: 0.1000 },
    ],
  },
  {
    pair: ['Right Vertical', 'Left Chest'],
    adjustments: [{ target: 'Left Chest', scaleW: 0.75, center: true }],
  },

  // ── Back conflicts ──────────────────────────────────────────────────────────
  {
    pair: ['Across Shoulder', 'Medium Back'],
    adjustments: [
      { target: 'Across Shoulder', scaleH: 0.65, center: true },
      { target: 'Medium Back', shiftYpct: 0.09 },
    ],
  },
  {
    pair: ['Across Shoulder', 'Back Center'],
    adjustments: [
      { target: 'Back Center', shiftYpct: 0.09 },
    ],
  },
];

/**
 * Returns a transform map: { [loc]: { shiftX, shiftY, scaleW, scaleH, center } }
 * by scanning CONFLICT_RULES and accumulating adjustments for active locations.
 */
function calculateAdjustedSizes(designLocs) {
  const transforms = {};
  designLocs.forEach(loc => {
    transforms[loc] = { shiftX: 0, shiftY: 0, scaleW: 1, scaleH: 1, center: false };
  });

  const locSet = new Set(designLocs);

  for (const rule of CONFLICT_RULES) {
    const [a, b] = rule.pair;
    if (!locSet.has(a) || !locSet.has(b)) continue;

    for (const adj of rule.adjustments) {
      const t = transforms[adj.target];
      if (!t) continue;
      if (adj.scaleW    !== undefined) t.scaleW  *= adj.scaleW;
      if (adj.scaleH    !== undefined) t.scaleH  *= adj.scaleH;
      if (adj.shiftYpct !== undefined) t.shiftY  += adj.shiftYpct;
      if (adj.shiftXpct !== undefined) t.shiftX  += adj.shiftXpct;
      if (adj.center)                  t.center   = true;
    }
  }

  return transforms;
}

// --- Load image helper --------------------------------------------------------
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('Failed: ' + src));
    img.src = src;
  });
}

// --- Printed-logo blending ---------------------------------------------------
function drawPrintedLogo(ctx, { logo, maskImg, x, y, w, h, sx, sy, sw, sh }) {
  const layer = document.createElement('canvas');
  layer.width  = Math.max(1, Math.round(w));
  layer.height = Math.max(1, Math.round(h));
  const lctx = layer.getContext('2d');

  const r            = 0.68;
  const baseAlpha    = 0.92 - 0.30 * r;
  const blendAlpha   = 0.08 + 0.38 * r;
  const textureAlpha = 0.06 + 0.22 * r;

  lctx.globalCompositeOperation = 'source-over';
  lctx.globalAlpha = baseAlpha;
  lctx.drawImage(logo, 0, 0, layer.width, layer.height);

  lctx.globalCompositeOperation = 'multiply';
  lctx.globalAlpha = blendAlpha;
  lctx.drawImage(maskImg, sx, sy, sw, sh, 0, 0, layer.width, layer.height);

  lctx.globalCompositeOperation = 'multiply';
  lctx.globalAlpha = textureAlpha;
  lctx.drawImage(maskImg, sx, sy, sw, sh, 0, 0, layer.width, layer.height);

  lctx.globalCompositeOperation = 'destination-in';
  lctx.globalAlpha = 1.0;
  lctx.drawImage(logo, 0, 0, layer.width, layer.height);

  ctx.drawImage(layer, x, y, w, h);
}

// =============================================================================
// --- ShirtPhotoCanvas --------------------------------------------------------
// =============================================================================

export function ShirtPhotoCanvas({ designs, isBack, isCircle, shirtColor, canvasStyle }) {
  const canvasRef = useRef(null);

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);
    const maskSrc = isBack ? MASK_BACK : MASK_FRONT;

    try {
      const maskImg   = await loadImage(maskSrc);
      const tintColor = resolveColor(shirtColor);

      if (tintColor) {
        const offscreen = document.createElement('canvas');
        offscreen.width  = W;
        offscreen.height = H;
        const offCtx = offscreen.getContext('2d');
        offCtx.drawImage(maskImg, 0, 0, W, H);
        offCtx.globalCompositeOperation = 'multiply';
        offCtx.fillStyle = tintColor;
        offCtx.fillRect(0, 0, W, H);
        offCtx.globalCompositeOperation = 'destination-in';
        offCtx.drawImage(maskImg, 0, 0, W, H);
        ctx.drawImage(offscreen, 0, 0);
      } else {
        ctx.drawImage(maskImg, 0, 0, W, H);
      }

      const designEntries = Object.entries(designs).filter(([, url]) => url);
      const designLocs    = designEntries.map(([loc]) => loc);
      const transforms    = calculateAdjustedSizes(designLocs);

      await Promise.all(
        designEntries.map(async ([loc, url]) => {
          const sleevePos = SLEEVE_SETTING[loc];
          const rectPos   = !sleevePos ? getLocationPhotoPosition(loc, isCircle) : null;
          if (!sleevePos && !rectPos) return;

          try {
            const logo = await loadImage(url);
            ctx.save();

            if (sleevePos) {
              // Sleeves are physically separate — no conflict adjustment needed
              const cx = (sleevePos.cx / 100) * W;
              const cy = (sleevePos.cy / 100) * H;
              const dw = (sleevePos.dw / 100) * W;
              const dh = (sleevePos.dh / 100) * H;

              ctx.translate(cx, cy);
              ctx.rotate(sleevePos.rot * Math.PI / 180);
              ctx.beginPath();
              if (sleevePos.cx > 50) ctx.rect(-dw / 2, -dh / 2, dw / 2, dh);
              else                   ctx.rect(0,        -dh / 2, dw / 2, dh);
              ctx.clip();

              drawPrintedLogo(ctx, {
                logo, maskImg,
                x: -dw / 2, y: -dh / 2, w: dw, h: dh,
                sx: cx - dw / 2, sy: cy - dh / 2, sw: dw, sh: dh,
              });

            } else if (rectPos && rectPos.clipShape === 'rect') {
              const t = transforms[loc] || { shiftX: 0, shiftY: 0, scaleW: 1, scaleH: 1, center: false };

              let x = parseFloat(rectPos.left)   / 100 * W;
              let y = parseFloat(rectPos.top)    / 100 * H;
              let w = parseFloat(rectPos.width)  / 100 * W;
              let h = parseFloat(rectPos.height) / 100 * H;

              const origW = w;
              const origH = h;

              w *= t.scaleW;
              h *= t.scaleH;

              // Re-center within original bounds after scaling, then apply shifts
              if (t.center) {
                x += (origW - w) / 2;
                y += (origH - h) / 2;
              }

              x += t.shiftX * W;
              y += t.shiftY * H;

              // Skip if hidden
              if (w < 2 || h < 2) { ctx.restore(); return; }

              drawPrintedLogo(ctx, {
                logo, maskImg,
                x, y, w, h,
                sx: x, sy: y, sw: w, sh: h,
              });
            }

            ctx.restore();
          } catch (e) {
            console.warn('Failed to load design for location:', loc, e);
          }
        })
      );

    } catch (e) {
      console.error('Canvas draw error:', e);
    }
  }, [designs, isBack, isCircle, shirtColor]);

  useEffect(() => { draw(); }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      width={736}
      height={980}
      style={{ width: '100%', ...(canvasStyle || {}) }}
    />
  );
}

// =============================================================================
// --- Modal -------------------------------------------------------------------
// =============================================================================

export default function ShirtPreviewModal({
  open,
  onClose,
  designImages   = {},
  isCircle       = false,
  shirtColor     = '',
  title,
  onSave,
  note,
  hideBackButton = false,
}) {
  const [activeTab, setActiveTab] = useState('front');

  const allEntries   = Object.entries(designImages).filter(([, v]) => v && typeof v === 'string');
  const backEntries  = allEntries.filter(([loc]) => BACK_LOCS.has(loc) || SLEEVE_LOCS.has(loc));
  const frontEntries = allEntries.filter(([loc]) => !BACK_LOCS.has(loc));
  const hasFront = frontEntries.length > 0;
  const hasBack  = backEntries.length > 0;
  const hasBoth  = hasFront && hasBack;

  useEffect(() => {
    if (open) setActiveTab(hasFront ? 'front' : 'back');
  }, [open, hasFront]);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [open, onClose]);

  if (!open) return null;

  const isBack = activeTab === 'back';
  const currentEntries = isBack ? backEntries : frontEntries;
  const currentDesigns = Object.fromEntries(
    currentEntries.map(([loc, url]) => {
      if (isBack && loc === 'Left Sleeve')  return ['Left Sleeve Back',  url];
      if (isBack && loc === 'Right Sleeve') return ['Right Sleeve Back', url];
      return [loc, url];
    })
  );
  const heading = title != null ? title : (isBack ? 'Preview - Back' : 'Preview - Front');

  return (
    <div className="spm-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Shirt design preview">
      <div className="spm-modal" onClick={(e) => e.stopPropagation()}>

        <div className="spm-header">
          <h2 className="spm-title">{heading}</h2>
          <button className="spm-close" onClick={onClose} aria-label="Close preview">X</button>
        </div>

        {hasBoth && (
          <div className="spm-tabs">
            <button
              className={'spm-tab' + (activeTab === 'front' ? ' spm-tab--active' : '')}
              onClick={() => setActiveTab('front')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M20 7H4a2 2 0 00-2 2v6a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" />
                <path d="M12 11v2M12 17h.01" />
              </svg>
              Front
            </button>
            <button
              className={'spm-tab' + (activeTab === 'back' ? ' spm-tab--active' : '')}
              onClick={() => setActiveTab('back')}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
              </svg>
              Back
            </button>
          </div>
        )}

        <div className="spm-shirt-wrapper spm-shirt-wrapper--photo">
          <ShirtPhotoCanvas
            key={activeTab + '-' + JSON.stringify(currentDesigns) + '-' + shirtColor}
            designs={currentDesigns}
            isBack={isBack}
            isCircle={isCircle}
            shirtColor={shirtColor}
          />
          {currentEntries.length === 0 && (
            <div className="spm-empty-side">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9l4-4 4 4 4-6 4 6" />
                <circle cx="8.5" cy="8.5" r="1.5" />
              </svg>
              <span>No design on this side</span>
            </div>
          )}
        </div>

        {currentEntries.length > 0 && (
          <div className="spm-legend">
            {currentEntries.map(([loc]) => (
              <span key={loc} className="spm-legend-chip">
                <span className="spm-legend-dot" />
                {loc}
              </span>
            ))}
          </div>
        )}

        <p className="spm-note">
          {note != null ? note : 'Approximate preview — final print position may vary slightly.'}
        </p>

        <div className="spm-footer">
          {!hideBackButton && (
            <button className="spm-btn spm-btn--secondary" onClick={onClose}>
              &larr; Back to Editor
            </button>
          )}
          {onSave && (
            <button className="spm-btn spm-btn--primary" onClick={() => { onClose(); onSave(); }}>
              &#10003; Save &amp; Continue
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
